import { ComponentMatrixRowHeaderCell } from './componentMatrixRowHeaderCell';
import { ComponentMatrix2Commands, RowElementModel } from '../types';
import { ArdoqId } from '@ardoq/api-types';

type ComponentMatrixRowHeaderArgs = {
  rowModel: RowElementModel;
  rowIndex: number;
  /** Per-row gap amounts (indexed by global row index). Used to compute the
   * bottom gap for cells at any level whose last leaf row is a boundary row. */
  rowBottomGaps: number[];
  /** Per-row track heights (indexed by global row index). Used to size the
   * stripe of expanded row headers to the height of their first sub-row. */
  rowHeights: number[];
  hoveredComponentId: ArdoqId | null;
  focusedComponentId: ArdoqId | null;
  contextComponentId: ArdoqId | null;
  commands: ComponentMatrix2Commands;
};
const ComponentMatrixRowHeader = ({
  rowModel,
  rowIndex,
  rowBottomGaps,
  rowHeights,
  hoveredComponentId,
  focusedComponentId,
  contextComponentId,
  commands,
}: ComponentMatrixRowHeaderArgs) => {
  // Content height of this rowModel's first sub-row (its track height minus
  // that row's encoded inter-group gap). Used as the stripe height for
  // expanded groups so the stripe matches the first sub-row's body.
  //
  // Why `rowHeights[rowIndex]` (not `rowHeights[rowIndex + 1]`):
  // expanded groups don't get their own grid row track. They span over their
  // children's rows via `grid-row: span N`, anchored at the first child's
  // grid row. `getRowHeaderModels` consumes `pendingHeaderCells` on the first
  // leaf, so the parent's headerCell ends up in the rowModel for the topmost
  // sub-row — i.e. `rowIndex` IS the first sub-row's grid track.
  //
  // Same value for every cell in this row, so hoisted out of the headerCells map.
  const firstRowHeight =
    (rowHeights[rowIndex] ?? 0) - (rowBottomGaps[rowIndex] ?? 0);
  return (
    <>
      {rowModel.headerCells.map((headerCell, headerCellIndex) => {
        const cellBottomGap =
          rowBottomGaps[rowIndex + headerCell.span - 1] ?? 0;
        return (
          <ComponentMatrixRowHeaderCell
            key={headerCellIndex}
            headerCell={headerCell}
            rowIndex={rowIndex}
            cellBottomGap={cellBottomGap}
            firstRowHeight={firstRowHeight}
            hoveredComponentId={hoveredComponentId}
            focusedComponentId={focusedComponentId}
            contextComponentId={contextComponentId}
            commands={commands}
          />
        );
      })}
    </>
  );
};
export { ComponentMatrixRowHeader };
